'use client';

import './lead-form.scss';

import React, { useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import { EmailField } from '@sola/ark-core/molecules/email-field/email-field.js';
import { Form, FormSubmit } from '@sola/ark-core/molecules/form/form.js';
import { InputField } from '@sola/ark-core/molecules/input-field/input-field.js';
import { PhoneField } from '@sola/ark-core/molecules/phone-field/phone-field.js';
import { PostalCodeField } from '@sola/ark-core/molecules/postal-code-field/postal-code-field.js';
import { Select } from '@sola/ark-core/molecules/select/select.js';
import { StateField } from '@sola/ark-core/molecules/state-field/state-field.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { CountryCode, StateEnum } from '@sola/schema/dotcom/shared/geo.js';
import {
  LeadFormChurchCityMaxLength,
  LeadFormFirstNameMaxLength,
  LeadFormLastNameMaxLength,
} from '@sola/skippy-sdk/constants.js';
import { trackLeadFormSuccess } from '@sola/tracking/components/datalayer.js';

interface LeadFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  audienceSize: string;
  churchName: string;
  churchAddress: string;
  churchCity: string;
  churchState: string;
  churchPostal: string;
  churchCountry: string;
}

export type LeadFormProps = {
  buttonText?: string;
  onSubmit: (data: LeadFormData) => Promise<boolean>;
};

const LeadFormState = z.object({
  isSubmitting: z.boolean().optional(),
  errorMessage: z.string().optional(),
  showThankYou: z.boolean().optional(),
});
type LeadFormState = z.infer<typeof LeadFormState>;

const makeOption = (value: string) => {
  return { text: value, value };
};

export const LeadForm: React.FC<LeadFormProps> = ({ buttonText, onSubmit }) => {
  const { t } = useTranslation();
  const [formState, setFormState] = useState<LeadFormState>({});
  const defaultCountry = 'US' as CountryCode;
  const shouldValidate = true;

  const roleOptions = [
    { text: t('skippy.form.roleOptions.defaultText'), value: '' },
    ...[
      t('skippy.form.roleOptions.pastor'),
      t('skippy.form.roleOptions.churchStaff'),
      t('skippy.form.roleOptions.congregant'),
    ].map(makeOption),
  ];

  const sizeOptions = [
    { text: t('skippy.form.sizeOptions.defaultText'), value: '' },
    ...[
      '1-49',
      '50-99',
      '100-199',
      '200-299',
      '300-399',
      '400-499',
      '500-749',
      '750-999',
      '1,000',
      '2,000',
      '3,000',
      '4,000',
      '5,000',
      '6,000',
      '7,000',
      '8,000',
      '9,000',
      '10,000+',
    ].map(makeOption),
  ];

  const stateOptions = [
    { text: t('skippy.form.stateOptions.defaultText'), value: '' },
    ...Object.entries(StateEnum).map(([abbr, name]) => ({
      text: name,
      value: abbr,
    })),
  ];

  const handleSubmit = async (data: FieldValues) => {
    let errorMessage = '';

    setFormState((prevState) => ({
      ...prevState,
      errorMessage,
      isSubmitting: true,
    }));

    const {
      firstName,
      lastName,
      email,
      phone,
      role,
      audienceSize,
      churchName,
      churchAddress,
      churchCity,
      churchState,
      churchPostal,
    } = data;

    try {
      if (
        await onSubmit({
          firstName,
          lastName,
          email,
          phone,
          role,
          audienceSize,
          churchName,
          churchAddress,
          churchCity,
          churchState,
          churchPostal,
          churchCountry: defaultCountry,
        })
      ) {
        // @TODO: Pass the subscriptionCode to this component so we can track it accurately.
        trackLeadFormSuccess('Lead Form');
        setFormState({
          showThankYou: true,
          isSubmitting: false,
        });

        return;
      }

      errorMessage = t('skippy.form.errorVerify');
    } catch {
      errorMessage = t('skippy.form.errorSave');
    }

    if (errorMessage) {
      setFormState({
        isSubmitting: false,
        errorMessage,
      });
    }
  };

  if (formState.showThankYou) {
    return (
      <Alert
        variant="positive"
        message={t('skippy.form.leadCreate.thankYou')}
        scrollBehavior="instant"
        scrollPosition="center"
        scrollIntoView
      />
    );
  }

  return (
    <div className="LeadForm max-w-sm">
      {formState.errorMessage && (
        <div className="mb-comfy-xs">
          <Alert
            className="scroll-mt-roomy-md"
            variant="negative"
            message={formState.errorMessage}
            scrollIntoView
          />
        </div>
      )}

      <Form
        className="flex flex-col gap-comfy-sm @sm:gap-comfy-lg"
        onSubmit={handleSubmit}
      >
        <div className="grid grid-cols-1 @sm:grid-cols-2 gap-comfy-sm">
          <InputField
            label={t('skippy.form.contactFields.firstNameLabel')}
            name="firstName"
            required
            maxLength={LeadFormFirstNameMaxLength}
          />

          <InputField
            label={t('skippy.form.contactFields.lastNameLabel')}
            name="lastName"
            required={shouldValidate}
            maxLength={LeadFormLastNameMaxLength}
          />
        </div>

        <Select
          label={t('skippy.form.contactFields.roleLabel')}
          name="role"
          required={shouldValidate}
          items={roleOptions}
        />

        <EmailField
          name="email"
          label={t('skippy.form.contactFields.emailLabel')}
          required={shouldValidate}
        />

        <PhoneField
          label={t('skippy.form.contactFields.phoneLabel')}
          name="phone"
          countryCode={defaultCountry}
        />

        <InputField
          label={t('skippy.form.churchFields.churchNameLabel')}
          name="churchName"
          required={shouldValidate}
        />

        <Select
          label={t('skippy.form.churchFields.churchSizeLabel')}
          name="audienceSize"
          required={shouldValidate}
          items={sizeOptions}
        />

        <InputField
          label={t('skippy.form.churchFields.churchAddressLabel')}
          name="churchAddress"
          required={shouldValidate}
        />

        <InputField
          label={t('skippy.form.churchFields.churchCityLabel')}
          name="churchCity"
          required={shouldValidate}
          maxLength={LeadFormChurchCityMaxLength}
        />

        <div className="grid grid-cols-1 @sm:grid-cols-2 gap-comfy-sm">
          <StateField
            label={t('skippy.form.churchFields.churchStateLabel')}
            name="churchState"
            autoComplete="address-level1"
            required={shouldValidate}
            items={stateOptions}
            isUS
          />

          <PostalCodeField
            label={t('skippy.form.churchFields.churchPostalLabel')}
            name="churchPostal"
            autoComplete="postal-code"
            countryCode={defaultCountry}
            shouldValidate={shouldValidate}
          />
        </div>

        <FormSubmit
          isBusy={formState.isSubmitting}
          iconAfter={<ArkIcon iconName="arrow-right" />}
        >
          {buttonText || t('skippy.form.leadCreate.buttonText')}
        </FormSubmit>
      </Form>
    </div>
  );
};
